/* By Layla Saleh - smooth page scrolling.
 *
 * Uses Lenis 1.3.26 (MIT, darkroom.engineering), loaded from jsDelivr pinned to
 * that exact version with a sha384 integrity hash - see the script tag in
 * sections/ui-overlays.liquid. Delete that tag and this one and the site
 * scrolls natively again, exactly as before.
 *
 * WHY THE WHOLE PAGE, NOT JUST ONE SECTION
 * ----------------------------------------
 * A recording of the homepage tracked frame by frame showed the page itself
 * moving in steps of roughly 70 px every tenth of a second on a mouse wheel,
 * well before it reached any scripted section. That is how a wheel scrolls a
 * page natively on Windows, and every section did it. It is only noticed on
 * "In Demand" because that is where scrolling drives visible motion - a
 * sideways glide can only ever be as smooth as the scroll feeding it. So the
 * fix is upstream: smooth the scroll, and everything driven by it follows.
 *
 * WHAT IT LEAVES ALONE
 * --------------------
 * - Touch. syncTouch is off, so phones keep their own native momentum
 *   scrolling, which is already smooth. Lenis only takes over mouse wheels and
 *   trackpads.
 * - Anything that scrolls on its own: drawers, the cart, search, account
 *   menus, country and language lists, filters, the try-on modal, the chat
 *   widget. allowNestedScroll catches any scrollable box automatically, and
 *   `prevent` names the theme's own ones explicitly as a second line.
 * - Open drawers. The theme marks <body> with .overflow-hidden whenever a
 *   drawer or modal is open; Lenis is stopped for exactly that long, so the
 *   page underneath can never be scrolled from inside a drawer.
 * - The Shopify theme editor, which scrolls to sections itself and would
 *   fight it.
 * - Anyone who has asked their device for reduced motion.
 *
 * GSAP INTEGRATION
 * ----------------
 * The theme ships GSAP and ScrollTrigger. Lenis is driven from GSAP's own
 * ticker and tells ScrollTrigger about every scroll on the same frame, which
 * is the integration Lenis documents. That is what makes a pinned section and
 * the scroll move together instead of the pin catching up a frame late.
 *
 * window.__blsSmooth.state is 'pending' until a decision is made, then 'on'
 * or 'off'. assets/indemand-scroll.js reads it to pick its own settings.
 */
(function () {
  'use strict';

  var S = window.__blsSmooth = { state: 'pending', lenis: null };

  var PREVENT = [
    'burger-drawer', 'cart-drawer', '.cart-drawer', '.drawer', '.menu-drawer',
    'details-modal', '.modal', '.search-modal', 'predictive-search',
    '.header__account-modal', '.disclosure__list', '.facets',
    '[role="dialog"]', '.vton__modal', 'shopify-chat'
  ].join(',');

  function decide(state) {
    S.state = state;
    try { document.dispatchEvent(new CustomEvent('bls:smooth', { detail: state })); } catch (e) {}
  }

  function reducedMotion() {
    try {
      return window.matchMedia &&
             window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) { return false; }
  }

  function start() {
    if (reducedMotion()) return decide('off');
    if (window.Shopify && window.Shopify.designMode) return decide('off');
    if (typeof window.Lenis !== 'function') return decide('off');

    var gsap = window.gsap, ST = window.ScrollTrigger;
    var viaTicker = !!(gsap && gsap.ticker && ST);

    var lenis = new window.Lenis({
      lerp: 0.1,
      smoothWheel: true,
      syncTouch: false,
      allowNestedScroll: true,
      autoRaf: !viaTicker,
      prevent: function (node) {
        return !!(node && node.closest && node.closest(PREVENT));
      }
    });

    if (viaTicker) {
      lenis.on('scroll', ST.update);
      gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
      gsap.ticker.lagSmoothing(0);
      /* On phones the address bar showing and hiding resizes the viewport
         mid-scroll, and every resize makes ScrollTrigger re-measure pins -
         which is a visible jump. Ignore those. */
      try { ST.config({ ignoreMobileResize: true }); } catch (e) {}
    }

    function syncLock() {
      if (document.body.classList.contains('overflow-hidden')) lenis.stop();
      else lenis.start();
    }
    new MutationObserver(syncLock).observe(document.body, {
      attributes: true, attributeFilter: ['class']
    });
    syncLock();

    S.lenis = lenis;
    decide('on');
  }

  function boot() {
    /* Lenis comes from a CDN and GSAP from the theme; give both up to ~3 s.
       If Lenis never arrives the site simply scrolls natively. */
    var tries = 0;
    (function wait() {
      var ready = typeof window.Lenis === 'function' && window.gsap && window.ScrollTrigger;
      if (ready || ++tries > 12) {
        try { start(); } catch (e) { decide('off'); }
        return;
      }
      setTimeout(wait, 250);
    })();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
